import { Link, useNavigate } from 'react-router-dom';

import { api } from '../../api/endpoints.js';
import { Page } from '../../components/AppShell.jsx';
import { Badge, Card, DataTable, Input, Pagination, Select } from '../../components/ui.jsx';
import { useListQuery } from '../../hooks/useListQuery.js';
import { money, humanise, date, number } from '../../lib/format.js';

/**
 * Broker inventory.
 *
 * Every rental listing in one place, which the panel did not have: the API
 * existed and the only way to reach a listing was to know which agency filed
 * it and open them. A listing waiting for review was effectively invisible.
 *
 * Approving, rejecting and suspending happen on the listing's own page, where
 * the photographs and the description a decision rests on are.
 */

const STATUS_TONE = {
  pending_verification: 'warning',
  active: 'success',
  rejected: 'danger',
  suspended: 'danger',
  rented: 'info',
};

/** The address as a person would say it, not as it is stored. */
function whereItIs(address = {}) {
  return [address.locality, address.city].filter(Boolean).join(', ') || address.line1 || '—';
}

export default function InventoryPage() {
  const navigate = useNavigate();

  const list = useListQuery({
    key: 'admin-broker-properties',
    fetcher: api.broker.properties,
    initialFilters: { status: '' },
    searchable: true,
  });

  const columns = [
    {
      key: 'listing',
      header: 'Listing',
      render: (property) => (
        <div>
          <Link className="table__link table__primary" to={`/inventory/${property._id}`}>
            {property.title || 'Untitled draft'}
          </Link>
          <div className="table__muted">
            {humanise(property.propertyType)} · {whereItIs(property.address)}
          </div>
        </div>
      ),
    },
    {
      key: 'broker',
      header: 'Listed by',
      render: (property) => {
        if (!property.broker) return <span className="table__muted">Owner, no agency</span>;

        // A server that does not resolve the agency sends its bare id.
        const agency = typeof property.broker === 'object' ? property.broker : { _id: property.broker };
        return (
          <Link
            className="table__link"
            to={`/brokers/${agency._id}`}
            onClick={(event) => event.stopPropagation()}
          >
            {agency.businessName || 'View agency'}
          </Link>
        );
      },
    },
    {
      key: 'rent',
      header: 'Rent',
      className: 'table__numeric',
      render: (property) => money(property.rentMinor),
    },
    {
      key: 'views',
      header: 'Views',
      className: 'table__numeric',
      render: (property) => number(property.stats?.viewCount || 0),
    },
    {
      key: 'uniqueViews',
      header: 'Unique views',
      className: 'table__numeric',
      render: (property) => number(property.stats?.uniqueViewCount || 0),
    },
    {
      key: 'status',
      header: 'Status',
      render: (property) => (
        <Badge status={property.status} tone={STATUS_TONE[property.status]} />
      ),
    },
    {
      key: 'created',
      header: 'Filed',
      render: (property) => <span className="table__muted">{date(property.createdAt)}</span>,
    },
  ];

  return (
    <Page title="Inventory" subtitle="Every rental listing brokers and owners have filed.">
      <Card bodyless>
        <div className="card__body">
          <div className="toolbar">
            <div className="toolbar__search">
              <Input
                placeholder="Search by title or city"
                value={list.search}
                onChange={(event) => list.setSearch(event.target.value)}
                aria-label="Search listings"
              />
            </div>

            <Select
              value={list.filters.status}
              onChange={(event) => list.setFilter('status', event.target.value)}
              aria-label="Filter by status"
            >
              <option value="">Any status</option>
              <option value="draft">Draft</option>
              <option value="pending_verification">Waiting for review</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="rented">Rented</option>
              <option value="rejected">Rejected</option>
              <option value="suspended">Suspended</option>
              <option value="archived">Archived</option>
            </Select>
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={list.rows}
          query={list.query}
          onRowClick={(property) => navigate(`/inventory/${property._id}`)}
          empty={{
            title: 'No listings here',
            body: 'Nothing matches these filters. Clear them if you expected to see something.',
          }}
        />

        <Pagination meta={list.meta} page={list.page} onPageChange={list.setPage} />
      </Card>
    </Page>
  );
}
